import { useEffect, useState } from "react";
import schoolLogo from "./assets/namhae-high-school-logo.png";
import {
  ArrowRight,
  ArrowLeft,
  BookOpenCheck,
  Check,
  FileUp,
  GraduationCap,
  LockKeyhole,
  School,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

const schoolUses = [
  {
    icon: GraduationCap,
    audience: "학생",
    title: "우리 반과 동아리 이야기를 한곳에",
    body: "학급 질문, 동아리 일정, 학생회 준비처럼 빠르게 의견을 모아야 하는 대화를 이어갑니다.",
    tags: ["학급", "동아리", "학생회"],
  },
  {
    icon: School,
    audience: "교사",
    title: "안내와 협업을 더 가볍게",
    body: "학급 안내, 교직원 소모임, 업무별 협의를 대화방으로 나누고 필요한 자료를 함께 공유합니다.",
    tags: ["학급 안내", "교직원 협업", "업무방"],
  },
  {
    icon: Users,
    audience: "학생 · 교사",
    title: "질문부터 자료 공유까지 빠르게",
    body: "짧은 질문과 답변, 일정 확인, 사진과 파일을 필요한 사람끼리 안전하게 주고받습니다.",
    tags: ["질문", "일정", "파일 공유"],
  },
];

const safetyPoints = [
  { icon: ShieldCheck, title: "남해타임 계정 연동", body: "별도 가입 없이 학교에서 사용하는 계정으로 연결합니다." },
  { icon: LockKeyhole, title: "참여자만 접근", body: "초대된 대화방의 메시지와 파일만 확인할 수 있습니다." },
  { icon: FileUp, title: "대화와 파일 분리 보관", body: "메시지와 첨부파일을 알맞은 저장공간에 나누어 관리합니다." },
];

function Home({ backend, onEnter }) {
  const connected = backend.database === "ready" && backend.storage === "ready";
  const [detailPage, setDetailPage] = useState(0);
  const [cycleToken, setCycleToken] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDetailPage((current) => (current + 1) % 3), 7000);
    return () => window.clearTimeout(timer);
  }, [detailPage, cycleToken]);

  const showDetailPage = (page) => {
    setDetailPage(page);
    setCycleToken((current) => current + 1);
  };

  return (
    <main className="home-page">
      <header className="home-nav">
        <a className="home-return-button" href="https://namhae.pages.dev/" aria-label="남해타임으로 돌아가기">
          <ArrowLeft className="home-return-arrow" size={17} aria-hidden="true" />
          <span className="home-return-wordmark official-wordmark official-wordmark--time" aria-hidden="true"><span>남해</span><span>타임</span></span>
          <strong>돌아가기</strong>
        </a>
        <button className="home-brand" type="button" disabled aria-label="NamTalk">
          <span className="home-brand-mark"><img src={schoolLogo} alt="" /></span>
          <span className="home-brand-copy"><strong>NamTalk</strong><small>남해고등학교</small></span>
        </button>
        <nav aria-label="남톡 소개 메뉴">
          <button className="home-start-button" type="button" onClick={onEnter} aria-label="남톡 시작">
            <span className="home-start-wordmark official-wordmark official-wordmark--talk" aria-hidden="true"><span>남</span><span>톡</span></span>
            <span className="home-start-label" aria-hidden="true">시작</span>
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </nav>
      </header>

      <div className="home-slide-stage">
        {detailPage === 0 && (
          <section className="home-hero home-slide" key="intro">
            <div className="hero-copy">
              <span className="eyebrow"><Sparkles size={15} /> NAMHAE HIGH SCHOOL · SCHOOL MESSENGER</span>
              <h1>학교의 오늘을<br />가볍게 이어주는 <button className="hero-namtalk-mark official-wordmark official-wordmark--talk" type="button" onClick={onEnter} aria-label="남톡 시작"><span aria-hidden="true">남</span><span aria-hidden="true">톡</span></button></h1>
              <p>남톡은 남해고등학교 학생과 교사를 위한 대화 공간입니다. 남해타임 계정으로 들어와 우리 반, 동아리, 학생회와 교직원 업무 이야기를 필요한 사람끼리 나눌 수 있습니다.</p>
              <div className="home-status" aria-live="polite">
                <i className={connected ? "home-status--ready" : ""} />
                <span>{backend.checking ? "학교 대화 서버를 확인하고 있어요" : connected ? "남톡 서버가 정상 운영 중입니다" : "안전한 연결을 준비하고 있습니다"}</span>
              </div>
            </div>

            <div className="hero-preview" aria-label="남해고등학교 학급 대화 예시">
              <div className="preview-school-bar">
                <span className="preview-school-avatar">2-3</span>
                <div><strong>2학년 3반</strong><small>학생 24명 · 담임교사</small></div>
                <span className="preview-live"><i /> 대화 중</span>
              </div>
              <div className="preview-room">
                <div className="preview-message preview-message--left"><b>담</b><div><span>담임 선생님 · 오후 4:12</span><p>내일 1교시 준비물과 학급 일정을 확인해 주세요.</p></div></div>
                <div className="preview-message preview-message--right"><div><span>나 · 오후 4:14</span><p>확인했습니다! 동아리 신청서는 오늘까지 맞나요?</p></div><b>나</b></div>
                <div className="preview-message preview-message--left"><b>담</b><div><span>담임 선생님 · 오후 4:15</span><p className="preview-file"><BookOpenCheck size={16} /> 2학기 학급 일정표.pdf</p></div></div>
              </div>
              <div className="preview-composer"><span>메시지를 입력하세요</span><ArrowRight size={17} /></div>
              <span className="floating-note floating-note--members"><Users size={15} /> 학생 · 교사 전용</span>
              <span className="floating-note floating-note--secure"><LockKeyhole size={15} /> 남해타임 계정으로 연결</span>
            </div>

            <div className="hero-promises" aria-label="남톡 핵심 특징">
              <span><Check size={15} /> 별도 회원가입 없이</span>
              <span><Check size={15} /> 필요한 사람끼리</span>
              <span><Check size={15} /> 메시지와 파일을 함께</span>
            </div>
          </section>
        )}

        {detailPage === 1 && (
          <section className="home-section home-slide" key="school-use">
            <div className="section-heading">
              <span>학교에서 이렇게 사용해요</span>
              <h2>학생과 교사에게 필요한 대화를<br />상황에 맞게 나눕니다.</h2>
              <p>공식 기록보다 빠른 소통이 필요할 때, 대상에 맞는 대화방을 만들어 질문과 자료를 이어갈 수 있습니다.</p>
            </div>
            <div className="feature-grid">
              {schoolUses.map(({ icon: Icon, audience, title, body, tags }) => (
                <article className="feature-card" key={audience}>
                  <div className="feature-card-top"><span className="feature-icon"><Icon size={22} /></span><em>{audience}</em></div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                  <div className="feature-tags">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                </article>
              ))}
            </div>
          </section>
        )}

        {detailPage === 2 && (
          <section className="service-role-section combined-role-safety home-slide" key="service-role">
            <div className="combined-role-copy">
              <div className="service-role-heading">
                <span>남해타임과 남톡의 역할</span>
                <h2>기록은 정확하게,<br />대화는 빠르고 편하게.</h2>
                <p>두 서비스가 같은 일을 반복하지 않고, 학교생활에서 각자 가장 잘하는 역할을 맡습니다.</p>
              </div>
              <div className="combined-safety-heading"><span>안심하고 대화할 수 있도록</span><h3>학교 구성원을 위한 기본을 지킵니다.</h3></div>
              <div className="combined-safety-list">
                {safetyPoints.map(({ icon: Icon, title, body }) => <article key={title}><span><Icon size={17} /></span><div><strong>{title}</strong><p>{body}</p></div></article>)}
              </div>
            </div>
            <div className="service-role-board">
              <article className="service-role-card service-role-card--time">
                <span className="service-role-logo service-role-logo--school"><img src={schoolLogo} alt="" /></span>
                <div className="service-role-brand-copy"><small>공식 학교 업무</small><h3 className="service-time-wordmark official-wordmark official-wordmark--time"><span>남해</span><span>타임</span></h3><em>NAMHAE TIME</em></div>
                <ul><li>시간표와 오늘의 학교</li><li>출결·평가·학교 업무</li><li>공식 공지와 오래 남길 기록</li></ul>
              </article>
              <div className="service-role-plus"><span><ArrowRight size={17} /></span><small>계정으로<br />바로 연결</small></div>
              <article className="service-role-card service-role-card--talk">
                <span className="service-role-logo service-role-logo--talk"><img src={schoolLogo} alt="" /></span>
                <div className="service-role-brand-copy"><small>빠른 학교 대화</small><h3 className="service-talk-wordmark official-wordmark official-wordmark--talk"><span>남</span><span>톡</span></h3><em>NAMTALK</em></div>
                <ul><li>학급·동아리·학생회 대화</li><li>교직원 협업과 질문</li><li>사진·파일·일정 공유</li></ul>
              </article>
            </div>
          </section>
        )}
      </div>

      <div className="home-page-switcher" role="group" aria-label="남톡 소개 화면 선택">
        {["홈", "학교 활용", "서비스 · 안전"].map((label, page) => (
          <button type="button" key={label} className={detailPage === page ? "active" : ""} aria-pressed={detailPage === page} onClick={() => showDetailPage(page)}><span>0{page + 1}</span>{label}</button>
        ))}
        <small>7초 자동 전환</small>
      </div>
      <footer className="home-policy-links"><a href="https://namhae.pages.dev/app/privacy/" target="_blank" rel="noreferrer">개인정보처리방침</a><span>·</span><a href="https://namhae.pages.dev/app/support/" target="_blank" rel="noreferrer">운영 문의 · 신고 도움</a></footer>
    </main>
  );
}

export default Home;
